import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  Settings, 
  Globe, 
  Shield,
  Zap,
  BookOpen,
  ArrowRight,
  CheckCircle,
  AlertCircle,
  Info
} from 'lucide-react';
import { Link } from 'react-router-dom';

export default function ChromeExtensionDocsPage() {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-4xl mx-auto px-6 py-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
              <Globe className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Chrome Extension</h1>
              <p className="text-gray-600">Browser-based memory capture for any webpage</p>
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            <Badge variant="default" className="bg-green-100 text-green-800">Ready to Use</Badge>
            <span className="text-sm text-gray-500">Setup time: ~2 minutes</span>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-12 space-y-8">
        
        {/* Quick Start */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Zap className="w-5 h-5 text-yellow-500" />
              Quick Start Guide
            </CardTitle>
            <CardDescription>
              Get your Chrome extension up and running in under 2 minutes
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            
            {/* Prerequisites */}
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <div className="flex items-center gap-2 mb-2">
                <Info className="w-5 h-5 text-blue-600" />
                <h4 className="font-medium text-blue-900">Prerequisites</h4>
              </div>
              <p className="text-blue-800 text-sm">
                A free Memara account is enough to get started. In the Chrome extension wizard you choose which memory space
                (and its integration connection) each build saves into—so captures always land in the right context. If you
                have not added a connection yet, follow the prompts in Integrations first; the builder needs a target to bind to.
              </p>
            </div>

            {/* Steps */}
            <div className="space-y-4">
              <div className="flex gap-4">
                <div className="flex-shrink-0 w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                  <span className="text-sm font-semibold text-purple-600">1</span>
                </div>
                <div className="flex-1">
                  <h4 className="font-medium text-gray-900 mb-2">Create Your Extension</h4>
                  <p className="text-gray-600 mb-3">
                    Open{' '}
                    <Link to="/dashboard?view=integrations" className="text-purple-600 hover:text-purple-700 underline">
                      Integrations
                    </Link>
                    , add Chrome Extension, and select which space and connection this build should use.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0 w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                  <span className="text-sm font-semibold text-purple-600">2</span>
                </div>
                <div className="flex-1">
                  <h4 className="font-medium text-gray-900 mb-2">Download & Install</h4>
                  <p className="text-gray-600 mb-3">
                    Download your personalized .crx file, open Chrome&apos;s extensions page at{' '}
                    <code className="text-sm bg-gray-100 px-1 rounded">chrome://extensions</code>, enable{' '}
                    <strong>Developer mode</strong>, then drag the .crx onto the page and confirm when prompted. No API keys
                    to paste—it is pre-configured.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0 w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                  <span className="text-sm font-semibold text-purple-600">3</span>
                </div>
                <div className="flex-1">
                  <h4 className="font-medium text-gray-900 mb-2">Start Capturing</h4>
                  <p className="text-gray-600 mb-3">
                    Visit any webpage, click your extension icon, and choose what to capture: full articles, 
                    selected text, or quick bookmarks.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0 w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                  <span className="text-sm font-semibold text-purple-600">4</span>
                </div>
                <div className="flex-1">
                  <h4 className="font-medium text-gray-900 mb-2">Find & Use</h4>
                  <p className="text-gray-600">
                    All captured content appears in your memory search, ready to enhance your AI conversations.
                  </p>
                </div>
              </div>
            </div>

            <div className="pt-4">
              <Button asChild className="w-full">
                <Link to="/dashboard?view=integrations">
                  Create Chrome Extension
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Link>
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Features */}
        <Card>
          <CardHeader>
            <CardTitle>Features & Capabilities</CardTitle>
            <CardDescription>What you can do with the Chrome extension</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-500 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-gray-900">Article Capture</h4>
                    <p className="text-sm text-gray-600">Clean extraction of full articles with smart formatting</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-500 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-gray-900">Text Selection</h4>
                    <p className="text-sm text-gray-600">Save highlighted text with surrounding context</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-500 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-gray-900">Quick Bookmarks</h4>
                    <p className="text-sm text-gray-600">One-click save of any webpage with title and URL</p>
                  </div>
                </div>
              </div>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-500 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-gray-900">Universal Compatibility</h4>
                    <p className="text-sm text-gray-600">Works reliably on all major websites</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-500 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-gray-900">Instant Search</h4>
                    <p className="text-sm text-gray-600">Full-text search across all captured content</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-500 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-gray-900">Multiple Extensions</h4>
                    <p className="text-sm text-gray-600">Create separate extensions for work, personal use, etc.</p>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Security */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-green-500" />
              Security & Privacy
            </CardTitle>
            <CardDescription>Built with security and privacy in mind</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-medium text-gray-900 mb-2">Pre-Authenticated</h4>
                <p className="text-sm text-gray-600">No API keys to manage or expose. Extensions come pre-configured with encrypted tokens.</p>
              </div>
              <div>
                <h4 className="font-medium text-gray-900 mb-2">Minimal Permissions</h4>
                <p className="text-sm text-gray-600">Only requests active tab access. No browsing history or personal data access.</p>
              </div>
              <div>
                <h4 className="font-medium text-gray-900 mb-2">Privacy-First</h4>
                <p className="text-sm text-gray-600">
                  Captured content stays in your memory spaces. The extension does not add its own analytics or ad
                  tracking beyond what Memara needs to operate the service (see the Privacy Policy for how the Memara
                  app and website handle data).
                </p>
              </div>
              <div>
                <h4 className="font-medium text-gray-900 mb-2">Secure Downloads</h4>
                <p className="text-sm text-gray-600">All extensions generated with unique encrypted tokens for your account.</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Troubleshooting */}
        <Card>
          <CardHeader>
            <CardTitle>Troubleshooting</CardTitle>
            <CardDescription>Common issues and solutions</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-4">
              <div className="border-l-4 border-yellow-400 bg-yellow-50 p-4">
                <div className="flex items-start gap-2">
                  <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-yellow-800">Extension not appearing in Chrome?</h4>
                    <p className="text-sm text-yellow-700 mt-1">
                      Make sure you've enabled "Developer mode" in chrome://extensions/ and that you dragged the .crx file directly to the extensions page.
                    </p>
                  </div>
                </div>
              </div>

              <div className="border-l-4 border-blue-400 bg-blue-50 p-4">
                <div className="flex items-start gap-2">
                  <Info className="w-5 h-5 text-blue-600 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-blue-800">Content not saving?</h4>
                    <p className="text-sm text-blue-700 mt-1">
                      Check that the integration for this build is still active. Open{' '}
                      <Link to="/dashboard?view=integrations" className="underline">
                        Integrations
                      </Link>{' '}
                      and verify the connection for the space this extension targets.
                    </p>
                  </div>
                </div>
              </div>

              <div className="border-l-4 border-green-400 bg-green-50 p-4">
                <div className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-green-800">Need help?</h4>
                    <p className="text-sm text-green-700 mt-1">
                      Email <a href="mailto:support@memara.io" className="underline">support@memara.io</a> or
                      visit our <Link to="/support" className="underline">support page</Link> for assistance.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Next Steps */}
        <Card>
          <CardHeader>
            <CardTitle>Next Steps</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Button asChild variant="outline">
                <Link to="/blog/memara-chrome-extension-spaces-teams">
                  <BookOpen className="w-4 h-4 mr-2" />
                  How-To: Spaces &amp; Teams
                </Link>
              </Button>
              <Button asChild variant="outline">
                <Link to="/blog/chrome-extension-integration-launch">
                  <BookOpen className="w-4 h-4 mr-2" />
                  Read Launch Article
                </Link>
              </Button>
              <Button asChild variant="outline">
                <Link to="/blog/chrome-extension-demo-walkthrough">
                  <BookOpen className="w-4 h-4 mr-2" />
                  Demo walkthrough (article)
                </Link>
              </Button>
              <Button asChild variant="outline">
                <Link to="/dashboard?view=integrations">
                  <Settings className="w-4 h-4 mr-2" />
                  Go to Integrations
                </Link>
              </Button>
              <Button asChild variant="outline">
                <Link to="/support">
                  <ArrowRight className="w-4 h-4 mr-2" />
                  Get Support
                </Link>
              </Button>
            </div>
          </CardContent>
        </Card>

      </div>
    </div>
  );
}
